import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import MoreHorizontal from 'lucide-react/dist/esm/icons/ellipsis.mjs';
import { placePopover, popoverBounds, popoverStyle } from './popoverPlacement.js';
/** The menu's width where there is room (gyro.css's max-width agrees). */
const OVERFLOW_MENU_WIDTH = 280;
function menuItems(menu) {
    return Array.from(menu?.querySelectorAll('[role="menuitem"]') ?? []);
}
/**
 * The floating header's "More actions" menu button (a WAI-ARIA menu button): the header buttons that do not fit beside the
 * title. Enter, Space or Down opens it on its first item, Up on its last; the arrows, Home and End move between items; Enter
 * or a click runs one; Escape closes it and returns to its button; Tab closes it and moves on from its button. A press
 * outside it closes it. Escape and the arrows stay with the menu, never reaching the panel's own keys.
 */
export function HeaderOverflowMenu({ items, onSelect, onFocusLost }) {
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState(undefined);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const id = useId();
    const focusLostRef = useRef(onFocusLost);
    focusLostRef.current = onFocusLost;
    // Removed while holding focus: the browser drops focus to the page's body once this is gone, so the host is told after.
    useLayoutEffect(() => {
        const root = rootRef.current;
        return () => {
            if (!root || !root.contains(root.ownerDocument.activeElement))
                return;
            const lost = focusLostRef.current;
            if (lost)
                setTimeout(lost, 0);
        };
    }, []);
    // Opened: the menu is measured and kept inside the window (popoverPlacement.ts), its right edge on its button where there
    // is room, so a button near the panel's left edge never pushes it off the screen.
    useLayoutEffect(() => {
        if (!open) {
            setStyle(undefined);
            return undefined;
        }
        const measure = () => {
            const button = triggerRef.current;
            const popup = menuRef.current;
            const container = rootRef.current;
            const view = button?.ownerDocument.defaultView;
            if (!button || !popup || !container || !view)
                return;
            const anchor = button.getBoundingClientRect();
            if (!(anchor.width > 0) && !(anchor.height > 0) && !(view.innerWidth > 0))
                return;
            const placed = placePopover({
                anchor,
                bounds: popoverBounds(view, container.closest('.gyro-panel')),
                width: Math.min(OVERFLOW_MENU_WIDTH, Math.max(popup.getBoundingClientRect().width, 200)),
                height: popup.getBoundingClientRect().height,
                align: 'end',
                side: 'below',
                gap: 6,
            });
            const next = popoverStyle(placed, container);
            setStyle((previous) => previous && Object.keys(next).every((key) => previous[key] === next[key]) ? previous : next);
        };
        measure();
        const view = triggerRef.current?.ownerDocument.defaultView;
        view?.addEventListener('resize', measure);
        view?.visualViewport?.addEventListener('resize', measure);
        return () => {
            view?.removeEventListener('resize', measure);
            view?.visualViewport?.removeEventListener('resize', measure);
        };
    }, [open]);
    // Opened: the first item (or the last, from Up) takes focus before the menu paints.
    useLayoutEffect(() => {
        if (!open)
            return;
        const list = menuItems(menuRef.current);
        (open === 'last' ? list[list.length - 1] : list[0])?.focus();
    }, [open]);
    useEffect(() => {
        if (!open)
            return undefined;
        const outside = (event) => {
            const root = rootRef.current;
            if (root && !event.composedPath().includes(root))
                setOpen(false);
        };
        document.addEventListener('pointerdown', outside, true);
        return () => document.removeEventListener('pointerdown', outside, true);
    }, [open]);
    const close = (refocus) => {
        setOpen(false);
        if (refocus)
            triggerRef.current?.focus();
    };
    const menuKey = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close(true);
            return;
        }
        // Tab leaves the menu from its button: the browser then moves focus on, forwards or back, as from the button.
        if (event.key === 'Tab') {
            event.stopPropagation();
            close(true);
            return;
        }
        const list = menuItems(menuRef.current);
        if (!list.length)
            return;
        const current = list.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown' ? (current + 1) % list.length
            : event.key === 'ArrowUp' ? (current - 1 + list.length) % list.length
                : event.key === 'Home' ? 0
                    : event.key === 'End' ? list.length - 1
                        : -1;
        if (next < 0)
            return;
        event.preventDefault();
        event.stopPropagation();
        list[next]?.focus();
    };
    // Spans, not divs: the header's button row is a span (phrasing content).
    return _jsxs("span", { className: "gyro-head-more", ref: rootRef, children: [_jsx("button", { ref: triggerRef, type: "button", className: `gyro-btn ghost gyro-head-more-trigger${open ? ' active' : ''}`, title: "More actions", "aria-label": "More actions", "aria-haspopup": "menu", "aria-expanded": Boolean(open), "aria-controls": open ? id : undefined, onClick: () => setOpen((current) => current ? false : 'first'), onKeyDown: (event) => {
                    if (event.key === 'Escape' && open) {
                        event.preventDefault();
                        event.stopPropagation();
                        close(true);
                        return;
                    }
                    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')
                        return;
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen(event.key === 'ArrowUp' ? 'last' : 'first');
                }, children: _jsx(MoreHorizontal, { size: 15, "aria-hidden": "true" }) }), open && items.length > 0 && _jsx("span", { ref: menuRef, id: id, role: "menu", "aria-label": "More actions", className: "gyro-head-more-menu", style: style, onKeyDown: menuKey, children: items.map((item) => _jsxs("button", { type: "button", role: "menuitem", tabIndex: -1, className: "gyro-head-more-item", "data-header-item": item.id, onClick: () => { close(true); onSelect(item.id); }, children: [item.icon, _jsx("span", { children: item.label })] }, item.id)) })] });
}
//# sourceMappingURL=HeaderOverflowMenu.js.map